/**
 * airportTimezones.js — M16B-03D
 * --------------------------------
 * Curated IATA → IANA timezone map for medical-tourism corridors
 * relevant to TreatX (Türkiye / EU hubs / select global departures).
 *
 * Used by ClinicalProfileEdit.jsx to derive timezone from a patient's
 * arrival/departure airport so the UI does not ask the patient to
 * choose a timezone manually.  STORE=UTC, DISPLAY=LOCAL.
 *
 * Missing codes degrade safely to UTC (the helper returns null and
 * the caller stores `timezone: null, datetime_utc: <as-entered>`).
 */
const AIRPORT_TZ = {
  // ─── Türkiye (primary destination) ───
  IST: 'Europe/Istanbul',   // Istanbul Airport
  SAW: 'Europe/Istanbul',   // Sabiha Gökçen
  ESB: 'Europe/Istanbul',   // Ankara Esenboğa
  ADB: 'Europe/Istanbul',   // İzmir Adnan Menderes
  AYT: 'Europe/Istanbul',   // Antalya
  DLM: 'Europe/Istanbul',   // Dalaman
  BJV: 'Europe/Istanbul',   // Bodrum-Milas
  ADA: 'Europe/Istanbul',   // Adana
  TZX: 'Europe/Istanbul',   // Trabzon
  GZT: 'Europe/Istanbul',   // Gaziantep

  // ─── EU hubs ───
  LHR: 'Europe/London',
  LGW: 'Europe/London',
  STN: 'Europe/London',
  MAN: 'Europe/London',
  EDI: 'Europe/London',
  CDG: 'Europe/Paris',
  ORY: 'Europe/Paris',
  AMS: 'Europe/Amsterdam',
  FRA: 'Europe/Berlin',
  MUC: 'Europe/Berlin',
  BER: 'Europe/Berlin',
  DUS: 'Europe/Berlin',
  HAM: 'Europe/Berlin',
  ZRH: 'Europe/Zurich',
  GVA: 'Europe/Zurich',
  VIE: 'Europe/Vienna',
  MAD: 'Europe/Madrid',
  BCN: 'Europe/Madrid',
  LIS: 'Europe/Lisbon',
  FCO: 'Europe/Rome',
  MXP: 'Europe/Rome',
  LIN: 'Europe/Rome',
  ATH: 'Europe/Athens',
  CPH: 'Europe/Copenhagen',
  ARN: 'Europe/Stockholm',
  OSL: 'Europe/Oslo',
  HEL: 'Europe/Helsinki',
  DUB: 'Europe/Dublin',
  PRG: 'Europe/Prague',
  WAW: 'Europe/Warsaw',
  BUD: 'Europe/Budapest',
  BUH: 'Europe/Bucharest',
  OTP: 'Europe/Bucharest',

  // ─── Middle East / Gulf ───
  DXB: 'Asia/Dubai',
  AUH: 'Asia/Dubai',
  DOH: 'Asia/Qatar',
  KWI: 'Asia/Kuwait',
  RUH: 'Asia/Riyadh',
  JED: 'Asia/Riyadh',
  BAH: 'Asia/Bahrain',
  TLV: 'Asia/Jerusalem',
  AMM: 'Asia/Amman',
  BEY: 'Asia/Beirut',
  CAI: 'Africa/Cairo',
  SSH: 'Africa/Cairo',
  HBE: 'Africa/Cairo',

  // ─── North America (rarer but possible) ───
  JFK: 'America/New_York',
  EWR: 'America/New_York',
  LGA: 'America/New_York',
  BOS: 'America/New_York',
  IAD: 'America/New_York',
  ATL: 'America/New_York',
  MIA: 'America/New_York',
  ORD: 'America/Chicago',
  DFW: 'America/Chicago',
  IAH: 'America/Chicago',
  DEN: 'America/Denver',
  LAX: 'America/Los_Angeles',
  SFO: 'America/Los_Angeles',
  SEA: 'America/Los_Angeles',
  YYZ: 'America/Toronto',
  YUL: 'America/Toronto',
  YVR: 'America/Vancouver',

  // ─── Asia ───
  HKG: 'Asia/Hong_Kong',
  SIN: 'Asia/Singapore',
  KUL: 'Asia/Kuala_Lumpur',
  BKK: 'Asia/Bangkok',
  DEL: 'Asia/Kolkata',
  BOM: 'Asia/Kolkata',
  NRT: 'Asia/Tokyo',
  HND: 'Asia/Tokyo',
  ICN: 'Asia/Seoul',
  PEK: 'Asia/Shanghai',
  PVG: 'Asia/Shanghai',
  CAN: 'Asia/Shanghai',

  // ─── Africa / Oceania ───
  JNB: 'Africa/Johannesburg',
  CPT: 'Africa/Johannesburg',
  LOS: 'Africa/Lagos',
  NBO: 'Africa/Nairobi',
  SYD: 'Australia/Sydney',
  MEL: 'Australia/Melbourne',
  AKL: 'Pacific/Auckland',
};

/**
 * Resolve IATA airport code → IANA timezone string.
 * Returns `null` if the code is unknown or invalid.
 */
export function timezoneForAirport(iataCode) {
  if (!iataCode || typeof iataCode !== 'string') return null;
  return AIRPORT_TZ[iataCode.trim().toUpperCase()] || null;
}

/**
 * Convert a `<input type="datetime-local">` value ("YYYY-MM-DDTHH:mm")
 * plus a target IANA timezone into a UTC ISO-8601 string.
 *
 * If the timezone is unknown / null, we treat the input as already in
 * the user's local browser timezone and return a normal UTC ISO string;
 * the caller may still display the raw `local` field.
 */
export function localToUtcIso(localDatetimeStr, ianaTz) {
  if (!localDatetimeStr) return null;
  // Browser fallback — no airport TZ known.
  if (!ianaTz) {
    const d = new Date(localDatetimeStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }
  // Compute UTC offset of the target timezone at the chosen instant.
  // We build a synthetic UTC date, then ask Intl what wall-clock that
  // produces in `ianaTz`, and solve for the original UTC.
  try {
    // Parse parts manually so we don't get confused by browser TZ.
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localDatetimeStr);
    if (!m) return null;
    const [, y, mo, d, h, mi, s] = m;
    // Initial guess: treat the input as if it were UTC; then correct for offset.
    const guessUtcMs = Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0));
    const guessDate = new Date(guessUtcMs);
    // What wall-clock does `guessDate` produce in ianaTz?
    // NOTE: 'en-US' is intentional here — this is an INTERNAL PARSER, not a
    // user-facing display. It produces stable, predictable formatToParts output
    // (e.g. '2-digit' month/day) for timezone offset computation. Do NOT change
    // this to undefined/browser locale; user locale parts (e.g. localized
    // month names) would break the numeric parsing below.
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(guessDate).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    const wallMs = Date.UTC(
      +parts.year, +parts.month - 1, +parts.day,
      +parts.hour === 24 ? 0 : +parts.hour, +parts.minute, +parts.second,
    );
    const offsetMs = wallMs - guessUtcMs;
    return new Date(guessUtcMs - offsetMs).toISOString();
  } catch {
    const d = new Date(localDatetimeStr);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
}

/**
 * Format a stored `{datetime_local, datetime_utc, timezone}` block for
 * a viewer.  `viewer="patient"` → show the local datetime exactly as
 * the patient entered it (no conversion).  `viewer="clinic"` → show
 * the clinic's local time computed from `datetime_utc`.
 */
export function formatStoredDateTime(block, viewer, clinicTz) {
  if (!block || typeof block !== 'object') return '—';
  const { datetime_local, datetime_utc, timezone } = block;
  if (viewer === 'patient' && datetime_local) {
    return `${datetime_local}${timezone ? ` (${timezone})` : ''}`;
  }
  if (datetime_utc) {
    try {
      const d = new Date(datetime_utc);
      const tz = clinicTz || 'Europe/Istanbul';
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
        hour12: false,
      });
      return `${fmt.format(d)} ${tz}`;
    } catch {
      return datetime_utc;
    }
  }
  return '—';
}

export default AIRPORT_TZ;
